import { Button, Center, Select, Stack } from '@mantine/core';
import type { UseFieldReturnType } from '@mantine/form';
import { IconArrowRight } from '@tabler/icons-react';
import { useGetDebtor, useGetWorkflows } from 'api/index';
import { i18nKeys, useTranslation } from 'locales';
import qs from 'query-string';
import { useEffect, useState } from 'react';
import { isNonNullish, isNullish } from 'remeda';
import { useLoadLightDebtors } from 'shared/hooks/use-load-light-debtors';

type Step1Props = {
  onClickNext: VoidFunction;
  debtorField: UseFieldReturnType<string | null>;
  workflowField: UseFieldReturnType<string | null>;
};

export const Step1 = ({ onClickNext, debtorField, workflowField }: Step1Props) => {
  const { t } = useTranslation();
  const { debtor_id: debtorIdFromUrl } = qs.parse(location.search);
  const isDebtorLocked = isNonNullish(debtorIdFromUrl);

  const [searchValue, setSearchValue] = useState<string>('');
  const { data: workflowsResponse } = useGetWorkflows();
  const { lightDebtors: _lightDebtors } = useLoadLightDebtors({
    query: isDebtorLocked ? String(debtorIdFromUrl) : (searchValue ?? ''),
  });

  // #region Preselected debtor
  /* When creating a payment plan from a debtor's page, its id is passed in the URL.
    Since the lightDebtor request is capped at 1000 results, we need to fetch the debtor separately
    to ensure that the debtor is available in the select. */
  const { data: singleDebtor } = useGetDebtor(debtorIdFromUrl as string, undefined, {
    query: {
      enabled: isDebtorLocked,
    },
  });

  const singleDebtoForSelect = {
    value: String(singleDebtor?.data.id ?? ''),
    label: singleDebtor?.data.attributes.full_name ?? '',
  };
  // #endregion

  const lightDebtors = _lightDebtors.map(debtor => ({
    value: String(debtor.id),
    label: debtor.fullName,
  }));

  const workflows = (workflowsResponse?.data ?? []).map(({ id, attributes }) => ({
    value: String(id),
    label: attributes.name,
  }));

  useEffect(() => {
    workflowField.setValue(workflows[0]?.value);
  }, [workflows.length]);

  return (
    <Center>
      <Stack miw="33%" maw="50%" w="100%">
        <Select
          {...debtorField.getInputProps()}
          label={t(i18nKeys.PAYMENT_PLAN.WIZARD.STEP1.CLIENT)}
          data={isDebtorLocked ? [singleDebtoForSelect] : lightDebtors}
          searchValue={searchValue}
          onSearchChange={setSearchValue}
          searchable
          readOnly={isDebtorLocked}
          placeholder={t(i18nKeys.PAYMENT_PLAN.WIZARD.STEP1.CLIENT_PLACEHOLDER)}
        />
        <Select
          {...workflowField.getInputProps()}
          label={t(i18nKeys.PAYMENT_PLAN.WIZARD.STEP1.WORKFLOW)}
          data={workflows}
          allowDeselect={false}
        />
        <Button
          w="100%"
          mt="lg"
          disabled={isNullish(debtorField.getValue()) || isNullish(workflowField.getValue())}
          rightSection={<IconArrowRight stroke={1.5} size={20} />}
          onClick={onClickNext}
        >
          {t(i18nKeys.PAYMENT_PLAN.WIZARD.STEP1.NEXT)}
        </Button>
      </Stack>
    </Center>
  );
};
